// [#13] Lightweight internationalization. A tiny context + t() helper with English
// and Tagalog dictionaries — no external dependency. Screens call useI18n().t("key")
// and fall back to English (then the key) when a Tagalog string is missing, so the
// app stays usable while translation coverage grows incrementally.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { androidRestoreLanguage, LANGUAGE_STORAGE_KEY, persistAndroidRestoreLanguage } from "./androidRestoreSettings";

import { en, tl } from "./translations";

export type Lang = "en" | "tl";
type Dict = Record<string, string>;

const STORAGE_KEY = LANGUAGE_STORAGE_KEY;

export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: "en", label: "English", short: "EN" },
  { code: "tl", label: "Tagalog", short: "TL" },
];



const DICTS: Record<Lang, Dict> = { en, tl };

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string, fallback?: string) => string };
const I18nContext = createContext<Ctx>({ lang: "en", setLang: () => {}, t: (k, f) => f ?? k });

function initialLang(): Lang {
  const restored = androidRestoreLanguage();
  if (restored) return restored;
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === "en" || s === "tl") return s;
  } catch { /* ignore */ }
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    // The current language still works if its separate backup mirror is unavailable.
    void persistAndroidRestoreLanguage(lang).catch(() => {});
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);
  const t = (key: string, fallback?: string) => DICTS[lang][key] ?? en[key] ?? fallback ?? key;
  return <I18nContext.Provider value={{ lang, setLang: setLangState, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }

/** Translate seed catalog copy only while it still matches the English default. */
export function translateDefaultCopy(lang: Lang, key: string, source: string): string {
  return en[key] === source ? DICTS[lang][key] ?? source : source;
}
